import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type DialogContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const DialogContext = createContext<DialogContextValue | null>(null);

type DialogProps = {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function Dialog({ children, open, defaultOpen = false, onOpenChange }: DialogProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = open ?? internalOpen;

  const context = useMemo(
    () => ({
      open: isOpen,
      setOpen: (nextOpen: boolean) => {
        if (open === undefined) {
          setInternalOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
      }
    }),
    [isOpen, onOpenChange, open]
  );

  return <DialogContext.Provider value={context}>{children}</DialogContext.Provider>;
}

export function DialogTrigger({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('DialogTrigger must be used inside Dialog');
  }

  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-10 items-center justify-center rounded-md border border-border bg-background/90 px-4 text-sm font-semibold text-foreground transition hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background',
        className
      )}
      onClick={() => context.setOpen(true)}
      {...props}
    />
  );
}

export function DialogContent({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const context = useContext(DialogContext);

  if (!context) {
    throw new Error('DialogContent must be used inside Dialog');
  }

  if (!context.open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/55 p-4">
      <div className={cn('w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-elevated', className)} {...props}>
        <div className="flex justify-end">
          <button
            type="button"
            className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-secondary"
            onClick={() => context.setOpen(false)}
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <header className={cn('space-y-1.5 text-left', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h4 className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />;
}

export function DialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <footer className={cn('mt-4 flex items-center justify-end gap-2', className)} {...props} />;
}
